/**
 * Памятка ЧС (запрос владельца 23.07; карточки — редизайн 26.07 по артборду 1
 * Arena.ai): шаги по типу опасности (пожар/наводнение/эвакуация/БПЛА) —
 * интерактивные карточки-чекбоксы со счётчиком «N/M», сворачиваются в строку.
 * Тон спокойный, без восклицаний (анти-паника). Тексты — i18n `drill.{hazard}.*`.
 */
import { tr } from '@app/i18n'
import { useState } from 'preact/hooks'
import type { DrillHazard } from '../emergency/drill'
import { langTick } from '../state'
import { MEMO_ICONS } from './emergency-icons'
import { GLYPH_MEMO } from './glyphs'
import { useDragPanel } from './use-drag'

const STEP_COUNT: Readonly<Record<DrillHazard, number>> = {
  fire: 5,
  flood: 4,
  evac: 4,
  uav: 5,
}

const TITLE_KEY = {
  fire: 'drill.fire.title',
  flood: 'drill.flood.title',
  evac: 'drill.evac.title',
  uav: 'drill.uav.title',
} as const

export function EmergencyChecklist({
  hazard,
  variant = 'panel',
}: {
  hazard: DrillHazard
  /** panel — в потоке аварийной панели; map — фиксированно СВЕРХУ над картой. */
  variant?: 'panel' | 'map'
}) {
  // Своя подписка на язык: пропсы стабильны, и signals-оптимизация иначе
  // пропускает ре-рендер (родителя ре-рендерит, детей — нет).
  langTick.value
  // На герое раскрыта сразу (решение владельца 26.07 — памятка важнее места);
  // на полной карте — свёрнута в иконку, чтобы не заслонять маршрут.
  const [collapsed, setCollapsed] = useState(variant === 'map')
  const drag = useDragPanel(`memo-${variant}`)
  const tabDrag = useDragPanel(`memo-tab-${variant}`)
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set())

  const total = STEP_COUNT[hazard]

  if (collapsed) {
    const label = `${tr(TITLE_KEY[hazard])} — ${tr('drill.expand')}`
    return (
      <button
        type="button"
        ref={tabDrag.elRef as never}
        style={tabDrag.style}
        class={`em-checklist-tab em-checklist-tab--${variant}`}
        aria-label={label}
        title={label}
        onPointerDown={(e) => tabDrag.onDragStart(e as unknown as PointerEvent)}
        onClick={() => {
          if (!tabDrag.wasDragged()) setCollapsed(false)
        }}
      >
        {/* На карте — компактная иконка (в колонке инструментов), в панели — таблетка */}
        <span class="glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: GLYPH_MEMO }} />
        {variant === 'map' ? '' : ` ${label} · ${checked.size}/${total}`}
      </button>
    )
  }

  const steps = Array.from({ length: total }, (_, i) =>
    tr(`drill.${hazard}.step${i + 1}` as Parameters<typeof tr>[0]),
  )

  const toggle = (i: number): void => {
    const next = new Set(checked)
    if (next.has(i)) next.delete(i)
    else next.add(i)
    setChecked(next)
  }

  return (
    <section
      ref={drag.elRef as never}
      style={drag.style}
      class={`em-checklist em-checklist--${variant}`}
      aria-label={tr(TITLE_KEY[hazard])}
    >
      <header
        class="em-checklist-head drag-handle"
        onPointerDown={(e) => drag.onDragStart(e as unknown as PointerEvent)}
      >
        <strong>
          <span class="glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: GLYPH_MEMO }} />{' '}
          {tr(TITLE_KEY[hazard])}
          <span class="em-checklist-count">
            {checked.size}/{total}
          </span>
        </strong>
        <button type="button" class="em-checklist-collapse" onClick={() => setCollapsed(true)}>
          {tr('drill.collapse')} ▾
        </button>
      </header>
      <div class="em-memo-grid">
        {steps.map((step, i) => {
          const on = checked.has(i)
          return (
            <button
              key={step}
              type="button"
              class={`em-memo-card${on ? ' em-memo-card--done' : ''}`}
              aria-pressed={on}
              onClick={() => toggle(i)}
            >
              {on ? (
                <span class="em-memo-check em-memo-check--done" aria-hidden="true">
                  ✓
                </span>
              ) : (
                <span
                  class="em-memo-check"
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: MEMO_ICONS[hazard]?.[i] ?? '' }}
                />
              )}
              <span class="em-memo-text">{step}</span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
